import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Archive,
  ArchiveRestore,
  ArrowLeft,
  BookOpen,
  Copy,
  Download,
  FileText,
  Loader2,
  Plus,
  Search,
  Trash2,
  TriangleAlert,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useProjeto } from '@/contexts/projeto-hook';
import {
  STATUS_MANUAL,
  arquivarManual,
  buscarManual,
  criarManual,
  duplicarManual,
  estatisticasManuais,
  formatarMomento,
  listarManuais,
  reativarManual,
  rotuloStatus,
  salvarManual,
  type EstatisticasManuais,
  type ManualResumo,
} from './manual-api';
import { dadosIniciais } from './manual-defaults';
import { serializarManual } from './manual-storage';
import { baixarArquivo, normalizarNomeArquivo } from './manual-utils';
import './estilos/manual-workspace.css';
import './estilos/manuais-lista.css';

/** Diálogo de criação: em branco ou copiando um manual já existente. */
function DialogoNovoManual({ manuais, ocupado, aoFechar, aoCriar }: {
  manuais: ManualResumo[];
  ocupado: boolean;
  aoFechar: () => void;
  aoCriar: (params: { nomeMaquina: string; baseId: string; limparEspecificos: boolean }) => void;
}) {
  const [nomeMaquina, setNomeMaquina] = useState('');
  const [baseId, setBaseId] = useState('');
  const [limparEspecificos, setLimparEspecificos] = useState(true);

  return (
    <div className="manuais-modal-fundo" role="dialog" aria-modal="true">
      <div className="manuais-modal">
        <header>
          <strong>Novo manual</strong>
          <button type="button" onClick={aoFechar} title="Fechar"><X /></button>
        </header>

        <label className="manual-field is-wide">
          Nome da máquina
          <input
            type="text"
            autoFocus
            value={nomeMaquina}
            placeholder="Ex.: Prensa hidráulica de buchas"
            onChange={(evento) => setNomeMaquina(evento.target.value)}
          />
          <small>Aparece na capa, no cabeçalho de todas as páginas e no histórico.</small>
        </label>

        <label className="manual-field is-wide">
          Partir de um manual existente
          <select value={baseId} onChange={(evento) => setBaseId(evento.target.value)}>
            <option value="">Começar em branco</option>
            {manuais.map((manual) => (
              <option key={manual.id} value={manual.id}>
                {manual.codigo} · {manual.nomeMaquina || manual.titulo}
              </option>
            ))}
          </select>
          <small>Copia o manual escolhido — inclusive normas, EPIs, textos institucionais e opções.</small>
        </label>

        {baseId && (
          <label className="manuais-check">
            <input
              type="checkbox"
              checked={limparEspecificos}
              onChange={(evento) => setLimparEspecificos(evento.target.checked)}
            />
            <span>
              Limpar o que é da máquina antiga
              <small>
                Apaga identificação, série, imagens, vistas, perigos, comandos, sequências e plano de
                manutenção. Desmarque só para gerar um manual gêmeo de uma máquina idêntica.
              </small>
            </span>
          </label>
        )}

        <footer>
          <button type="button" className="manual-ghost-button" onClick={aoFechar}>Cancelar</button>
          <button
            type="button"
            className="manual-primary-button is-compact"
            disabled={ocupado}
            onClick={() => aoCriar({ nomeMaquina: nomeMaquina.trim(), baseId, limparEspecificos })}
          >
            {ocupado ? <Loader2 className="gira" /> : <Plus />}
            Criar manual
          </button>
        </footer>
      </div>
    </div>
  );
}

export function ManuaisListPage() {
  const navigate = useNavigate();
  const { projeto } = useProjeto();
  const projetoId = projeto?.id ? String(projeto.id) : undefined;

  const [manuais, setManuais] = useState<ManualResumo[]>([]);
  const [estatisticas, setEstatisticas] = useState<EstatisticasManuais | null>(null);
  const [busca, setBusca] = useState('');
  const [status, setStatus] = useState('');
  const [mostrarArquivados, setMostrarArquivados] = useState(false);
  const [carregando, setCarregando] = useState(true);
  const [ocupado, setOcupado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [dialogoAberto, setDialogoAberto] = useState(false);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [lista, resumo] = await Promise.all([
        listarManuais({ projetoId, status: status || undefined, incluirInativos: mostrarArquivados }),
        estatisticasManuais(projetoId),
      ]);
      setManuais(lista);
      setEstatisticas(resumo);
      setErro(null);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível carregar os manuais.');
    } finally {
      setCarregando(false);
    }
  }, [mostrarArquivados, projetoId, status]);

  useEffect(() => { void carregar(); }, [carregar]);

  // A busca é local: a lista de um projeto cabe inteira na tela, e filtrar aqui
  // responde a cada tecla sem ida ao servidor.
  const visiveis = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return manuais;
    return manuais.filter((manual) => [
      manual.codigo, manual.titulo, manual.nomeMaquina, manual.identificacao,
      manual.modeloTipo, manual.fabricante, manual.contratante, manual.setor,
    ].some((campo) => (campo ?? '').toLowerCase().includes(termo)));
  }, [busca, manuais]);

  const abrir = (manual: ManualResumo) => navigate(`/manual-operacao?id=${manual.id}`);

  const criar = async ({ nomeMaquina, baseId, limparEspecificos }: {
    nomeMaquina: string;
    baseId: string;
    limparEspecificos: boolean;
  }) => {
    if (!projetoId) {
      setErro('Selecione um projeto antes de criar um manual.');
      return;
    }
    setOcupado(true);
    try {
      const novo = baseId
        ? await duplicarManual(baseId, { titulo: nomeMaquina || undefined, limparEspecificos })
        : await criarManual({
            projetoId,
            titulo: nomeMaquina || undefined,
            dados: { ...dadosIniciais(), nomeMaquina },
          });

      // A duplicação copia o documento sem saber o nome novo: grava também
      // dentro do JSON, senão a capa continuaria com o nome da máquina anterior.
      if (baseId && nomeMaquina) {
        const { dados } = await buscarManual(novo.id);
        await salvarManual(novo.id, { dados: { ...dados, nomeMaquina }, titulo: nomeMaquina });
      }

      navigate(`/manual-operacao?id=${novo.id}`);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível criar o manual.');
      setOcupado(false);
      setDialogoAberto(false);
    }
  };

  /**
   * Cópia rápida do card. A cópia integral (com identificação, vistas e
   * perigos da máquina antiga) fica no diálogo de novo manual, onde há espaço
   * para explicar o que cada opção leva junto.
   */
  const duplicar = async (manual: ManualResumo) => {
    if (!window.confirm(
      `Duplicar ${manual.codigo}?\n\nA cópia mantém normas, EPIs, textos institucionais e opções do documento, e começa com os dados da máquina em branco.`
    )) return;

    setOcupado(true);
    try {
      const copia = await duplicarManual(manual.id, { limparEspecificos: true });
      navigate(`/manual-operacao?id=${copia.id}`);
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível duplicar o manual.');
      setOcupado(false);
    }
  };

  const exportar = async (manual: ManualResumo) => {
    try {
      const { dados } = await buscarManual(manual.id);
      const nome = normalizarNomeArquivo(manual.nomeMaquina || manual.codigo);
      baixarArquivo(`manual-${nome}.json`, serializarManual(dados));
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível exportar o manual.');
    }
  };

  const arquivar = async (manual: ManualResumo) => {
    if (!window.confirm(`Arquivar ${manual.codigo}? Ele sai da lista, mas o histórico de versões é mantido.`)) return;
    await acao(() => arquivarManual(manual.id));
  };

  const excluir = async (manual: ManualResumo) => {
    if (!window.confirm(`Excluir ${manual.codigo} DEFINITIVAMENTE, com todas as versões e imagens? Não há como desfazer.`)) return;
    await acao(() => arquivarManual(manual.id, true));
  };

  const reativar = (manual: ManualResumo) => acao(() => reativarManual(manual.id).then(() => undefined));

  const acao = async (executar: () => Promise<void>) => {
    setOcupado(true);
    try {
      await executar();
      await carregar();
    } catch (falha) {
      setErro(falha instanceof Error ? falha.message : 'Não foi possível concluir a ação.');
    } finally {
      setOcupado(false);
    }
  };

  return (
    <div className="manual-workspace">
      <header className="manual-app-header">
        <div className="manual-app-brand">
          <button type="button" onClick={() => navigate('/projeto-selecao')} title="Voltar aos projetos"><ArrowLeft /></button>
          <span><BookOpen /></span>
          <div>
            <strong>Manuais de Operação</strong>
            <small>{projeto?.nome || 'Nenhum projeto selecionado'}</small>
          </div>
        </div>

        <div className="manual-app-progress">
          <span>{estatisticas ? `${estatisticas.total} manual${estatisticas.total === 1 ? '' : 'is'} · ${estatisticas.progressoMedio}% médio` : 'Carregando…'}</span>
          <div><i style={{ width: `${estatisticas?.progressoMedio ?? 0}%` }} /></div>
        </div>

        <div className="manual-app-actions">
          <button type="button" className="manual-primary-button" onClick={() => setDialogoAberto(true)}>
            <Plus />Novo manual
          </button>
        </div>
      </header>

      <section className="manuais-resumo">
        {STATUS_MANUAL.map((item) => (
          <button
            key={item.valor}
            type="button"
            className={status === item.valor ? 'is-ativo' : ''}
            title={item.descricao}
            onClick={() => setStatus(status === item.valor ? '' : item.valor)}
          >
            <strong>{estatisticas?.porStatus?.[item.valor] ?? 0}</strong>
            <span>{item.rotulo}</span>
          </button>
        ))}
        <div className={estatisticas?.pendenciasCriticas ? 'is-alerta' : ''}>
          <strong>{estatisticas?.pendenciasCriticas ?? 0}</strong>
          <span>pendências críticas</span>
        </div>
      </section>

      <section className="manuais-barra">
        <label className="manuais-busca">
          <Search />
          <input
            type="search"
            value={busca}
            placeholder="Buscar por código, máquina, TAG, fabricante ou contratante"
            onChange={(evento) => setBusca(evento.target.value)}
          />
        </label>
        <label className="manuais-check is-inline">
          <input type="checkbox" checked={mostrarArquivados} onChange={(evento) => setMostrarArquivados(evento.target.checked)} />
          <span>Mostrar arquivados</span>
        </label>
      </section>

      {erro && <p className="manuais-erro"><TriangleAlert />{erro}</p>}

      <main className="manuais-lista">
        {carregando ? (
          <p className="manuais-vazio"><Loader2 className="gira" />Carregando manuais…</p>
        ) : visiveis.length === 0 ? (
          <div className="manuais-vazio">
            <FileText />
            <strong>Nenhum manual {busca || status ? 'com esse filtro' : 'neste projeto'}</strong>
            <p>
              Cada manual é um documento completo de uma máquina, com histórico de versões próprio.
              Crie o primeiro ou copie um já existente para aproveitar normas, EPIs e textos padrão.
            </p>
            <button type="button" className="manual-primary-button is-compact" onClick={() => setDialogoAberto(true)}>
              <Plus />Novo manual
            </button>
          </div>
        ) : (
          visiveis.map((manual) => (
            <article key={manual.id} className={`manuais-card ${manual.isActive ? '' : 'is-arquivado'}`}>
              <button type="button" className="manuais-card-capa" onClick={() => abrir(manual)} title="Abrir manual">
                {manual.capaUrl ? <img src={manual.capaUrl} alt="" /> : <BookOpen />}
              </button>

              <div className="manuais-card-corpo">
                <header>
                  <strong onClick={() => abrir(manual)}>{manual.nomeMaquina || manual.titulo}</strong>
                  <span className={`manuais-pill is-${manual.status}`}>{rotuloStatus(manual.status)}</span>
                  {!manual.isActive && <span className="manuais-pill is-arquivado">Arquivado</span>}
                </header>

                <p className="manuais-card-meta">
                  {manual.codigo} · Rev. {String(manual.revisao).padStart(2, '0')}
                  {manual.identificacao ? ` · TAG ${manual.identificacao}` : ''}
                  {manual.fabricante ? ` · ${manual.fabricante}` : ''}
                  {manual.contratante ? ` · ${manual.contratante}` : ''}
                </p>

                <div className="manuais-card-progresso">
                  <div><i style={{ width: `${manual.progresso}%` }} /></div>
                  <span>{manual.progresso}%</span>
                  {manual.pendenciasCriticas > 0 && (
                    <em title="Pendências críticas"><TriangleAlert />{manual.pendenciasCriticas}</em>
                  )}
                </div>

                <p className="manuais-card-rodape">
                  {manual._count?.versoes ?? 0} versão{(manual._count?.versoes ?? 0) === 1 ? '' : 'ões'} ·
                  {' '}{manual.totalPaginas} pág. · atualizado {formatarMomento(manual.updatedAt)}
                  {manual.atualizadoPorNome ? ` por ${manual.atualizadoPorNome}` : ''}
                </p>
              </div>

              <div className="manuais-card-acoes">
                <button type="button" className="manual-primary-button is-compact" onClick={() => abrir(manual)}>Abrir</button>
                <button type="button" className="manual-ghost-button" disabled={ocupado} onClick={() => void duplicar(manual)} title="Criar um manual igual a este">
                  <Copy />Duplicar
                </button>
                <button type="button" className="manual-ghost-button" onClick={() => void exportar(manual)} title="Baixar o documento em JSON">
                  <Download />Exportar
                </button>
                {manual.isActive ? (
                  <button type="button" className="manual-ghost-button" disabled={ocupado} onClick={() => void arquivar(manual)}>
                    <Archive />Arquivar
                  </button>
                ) : (
                  <>
                    <button type="button" className="manual-ghost-button" disabled={ocupado} onClick={() => void reativar(manual)}>
                      <ArchiveRestore />Reativar
                    </button>
                    <button type="button" className="manual-ghost-button is-danger" disabled={ocupado} onClick={() => void excluir(manual)}>
                      <Trash2 />Excluir
                    </button>
                  </>
                )}
              </div>
            </article>
          ))
        )}
      </main>

      {dialogoAberto && (
        <DialogoNovoManual
          manuais={manuais.filter((manual) => manual.isActive)}
          ocupado={ocupado}
          aoFechar={() => setDialogoAberto(false)}
          aoCriar={(params) => void criar(params)}
        />
      )}
    </div>
  );
}

export default ManuaisListPage;
